"use client";

import { PortableText } from "@portabletext/react";
import React, { useState } from "react";

interface FAQ {
  _key: string;
  question: string;
  answer: any[];
  order: number;
}

interface FAQAccordionProps {
  faqs: FAQ[];
  title?: string;
  variant?: "default" | "homepage" | "campaign";
}

const FAQAccordion = ({
  faqs,
  title = "Frequently Asked Questions",
  variant = "default",
}: FAQAccordionProps) => {
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (key: string) => {
    // If clicking the same item that's already open, close it
    // Otherwise, close all and open only the clicked item
    if (openItems.has(key)) {
      setOpenItems(new Set());
    } else {
      setOpenItems(new Set([key]));
    }
  };

  const isOpen = (key: string) => openItems.has(key);

  return (
    <div className="faq-container w-full">
      {title && variant !== "homepage" && (
        <h2 className="mb-8 text-center text-3xl font-semibold text-gray-900 md:text-4xl">
          {title}
        </h2>
      )}
      <div
        className={`mx-auto px-4 sm:px-6 lg:px-8 ${variant === "homepage" ? "max-w-6xl" : "max-w-3xl"}`}
      >
        <div className="space-y-0">
          {faqs
            .sort((a, b) => a.order - b.order)
            .map((faq, index) => (
              <div
                key={faq._key}
                className={
                  index > 0
                    ? "border-t border-gray-200 pt-2 sm:pt-3 mt-1 sm:mt-1.5"
                    : ""
                }
              >
                <button
                  onClick={() => toggleItem(faq._key)}
                  className="flex w-full items-center justify-between py-2 text-left transition-colors sm:py-3"
                  aria-expanded={isOpen(faq._key)}
                >
                  <h3
                    className={`faq-question pr-4 font-normal text-gray-900 sm:pr-8 ${
                      variant === "homepage"
                        ? "text-xl sm:text-2xl md:text-3xl lg:text-[26px]"
                        : variant === "campaign"
                          ? "text-base sm:text-lg md:text-xl lg:text-[26px]"
                          : "text-base md:text-lg"
                    }`}
                  >
                    {faq.question}
                  </h3>
                  <div className="shrink-0">
                    <svg
                      className={`size-4 text-gray-500 transition-transform duration-200 sm:size-5 ${
                        isOpen(faq._key) ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen(faq._key)
                      ? "max-h-[1000px] opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="pb-2 pt-0 sm:pb-3">
                    <div className="prose prose-gray max-w-none">
                      {faq.answer && faq.answer.length > 0 ? (
                        <PortableText
                          value={faq.answer}
                          components={{
                            block: {
                              normal: (props: any) => (
                                <p
                                  className={`faq-answer mb-3 leading-relaxed text-gray-600 last:mb-0 ${
                                    variant === "homepage"
                                      ? "text-sm sm:text-base md:text-lg lg:text-[18px]"
                                      : variant === "campaign"
                                        ? "text-sm sm:text-base md:text-base lg:text-[16px]"
                                        : "text-base"
                                  }`}
                                >
                                  {props.children}
                                </p>
                              ),
                              h1: (props: any) => (
                                <h1 className="mb-3 text-2xl font-bold text-gray-900">
                                  {props.children}
                                </h1>
                              ),
                              h2: (props: any) => (
                                <h2 className="mb-3 text-xl font-bold text-gray-900">
                                  {props.children}
                                </h2>
                              ),
                              h3: (props: any) => (
                                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                                  {props.children}
                                </h3>
                              ),
                            },
                            marks: {
                              strong: (props: any) => (
                                <strong className="font-extrabold text-black">
                                  {props.children}
                                </strong>
                              ),
                              em: (props: any) => (
                                <em className="italic text-gray-600">
                                  {props.children}
                                </em>
                              ),
                              link: (props: any) => (
                                <a
                                  href={props.value?.href}
                                  className="text-blue-600 underline transition-colors hover:text-blue-800"
                                  target={
                                    props.value?.blank ? "_blank" : undefined
                                  }
                                  rel={
                                    props.value?.blank
                                      ? "noopener noreferrer"
                                      : undefined
                                  }
                                >
                                  {props.children}
                                </a>
                              ),
                            },
                            list: {
                              bullet: (props: any) => (
                                <ul
                                  className={`mb-4 ml-4 list-disc space-y-2 text-gray-600 last:mb-0 sm:ml-6 ${
                                    variant === "homepage"
                                      ? "text-sm sm:text-base md:text-lg lg:text-[18px]"
                                      : variant === "campaign"
                                        ? "text-sm sm:text-base md:text-base lg:text-[16px]"
                                        : "text-base"
                                  }`}
                                >
                                  {props.children}
                                </ul>
                              ),
                              number: (props: any) => (
                                <ol
                                  className={`mb-4 ml-4 list-decimal space-y-2 text-gray-600 last:mb-0 sm:ml-6 ${
                                    variant === "homepage"
                                      ? "text-sm sm:text-base md:text-lg lg:text-[18px]"
                                      : variant === "campaign"
                                        ? "text-sm sm:text-base md:text-base lg:text-[16px]"
                                        : "text-base"
                                  }`}
                                >
                                  {props.children}
                                </ol>
                              ),
                            },
                            listItem: {
                              bullet: (props: any) => (
                                <li className="leading-relaxed">
                                  {props.children}
                                </li>
                              ),
                              number: (props: any) => (
                                <li className="leading-relaxed">
                                  {props.children}
                                </li>
                              ),
                            },
                          }}
                        />
                      ) : (
                        <p className="italic text-gray-500">
                          No answer available
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default FAQAccordion;
